import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { includes } from 'lodash';
import { ENTITY_TYPE_ALBUM, META_TITLE_PREFIX } from '../../constants';

import * as facetActions from '../../actions/facet';

import { selectProfile } from '../../selectors/profile';
import {
  selectFilteredAndSortedAlbums,
  selectFilteredAndSortedAlbumsLoading,
  selectNextFilteredAlbumsCursor,
  selectPreviousFilteredAlbumsCursor,
} from '../../selectors/album';

import { selectActiveAlbumFacets, selectAlbumFilterTopFacets } from '../../selectors/facet';
import dataComponent from '../../hoc/dataComponent';
import { compose } from 'redux';
import { loadAlbumsByFilter } from '../../actions/album';
import { FormattedMessage } from 'react-intl';
import { messages } from '../messages/Profile';
import styles from '../common/FilteredList.css';

import FilteredList from '../common/FilteredList';
import { selectAlbumsFilterParams, selectSortOrder } from '../../selectors/filteredList';
import * as clientActions from '../../actions/client';
import { getBaseHelmet } from '../../utils/profile';
import AlbumListItem from '../common/AlbumListItem';
import * as analyticsActions from '../../actions/analytics';

class ProfileAlbums extends Component {
  static propTypes = {
    profileIsEnsemble: PropTypes.bool.isRequired,
    location: PropTypes.object.isRequired,
    filterParams: PropTypes.object.isRequired,
    activeFacets: PropTypes.array.isRequired,
    albums: PropTypes.array.isRequired,
    topFacets: PropTypes.object,
    nextCursor: PropTypes.string,
    loadAlbumsByFilter: PropTypes.func,
    sort: PropTypes.string.isRequired,
    currentCursor: PropTypes.string,
    prevCursor: PropTypes.string,
    albumsAreLoading: PropTypes.bool.isRequired,
    loadAllAlbumFacets: PropTypes.func.isRequired,
    analyticsTrack: PropTypes.func.isRequired,
    profileId: PropTypes.string.isRequired,
  };

  static onDataFetch(store, params, location, intl) {
    const state = store.getState();
    const profile = selectProfile(state, params.slug);
    const query = location.query;
    const filterParams = selectAlbumsFilterParams(query, profile.id);
    const { name } = profile;
    const title = intl.formatMessage(messages.profileAlbumsMetaTitle, { name });
    const description = intl.formatMessage(messages.profileAlbumsMetaDescription, { name });
    store.dispatch(
      clientActions.setHelmet({
        ...getBaseHelmet(profile, location, intl),
        title: META_TITLE_PREFIX + title,
        description,
        canonicalRoute: `/profiles/${params.slug}/albums`,
        keywords: intl.formatMessage(messages.profileAlbumsMetaKeywords, { name }),
        ogTitle: title,
        ogDescription: intl.formatMessage(messages.profileAlbumsOgDescription, { name }),
        currentCursor: params.cursor,
        nextCursor: selectNextFilteredAlbumsCursor(state, filterParams),
        prevCursor: selectPreviousFilteredAlbumsCursor(state, filterParams),
      })
    );
  }

  onItemClick = ({ content }) => {
    this.props.analyticsTrack('Selected Album from Profile', {
      id: content.id,
      profileId: this.props.profileId,
    });
  };

  getEmptyListMessage = () => {
    return (
      <FormattedMessage
        id="filtered-recording-list.no-albums"
        defaultMessage="No albums match these criteria."
      />
    );
  };

  renderAlbumListItem = album => {
    return (
      <li className={styles.item} key={album.id}>
        <AlbumListItem album={album} onClick={this.onItemClick} />
      </li>
    );
  };

  render() {
    const {
      albums,
      location,
      filterParams,
      topFacets,
      activeFacets,
      nextCursor,
      sort,
      prevCursor,
      currentCursor,
      albumsAreLoading,
      loadAllAlbumFacets,
      profileIsEnsemble,
    } = this.props;

    return (
      <FilteredList
        contextType="profile"
        contextFacet={profileIsEnsemble ? 'Ensemble' : 'Artist'}
        location={location}
        items={albums}
        filterType="albums"
        shouldRenderPlayAllButton={false}
        topFacets={topFacets}
        activeFacets={activeFacets}
        filterParams={filterParams}
        listIsEmpty={!albums || albums.length === 0}
        ignoredFacets={['albums']}
        nextCursor={nextCursor}
        prevCursor={prevCursor}
        loadMore={this.props.loadAlbumsByFilter}
        sort={sort}
        currentCursor={currentCursor}
        isLoading={albumsAreLoading}
        emptyListMessage={this.getEmptyListMessage()}
        renderFilteredListItem={this.renderAlbumListItem}
        loadAllFacets={loadAllAlbumFacets}
        gridLayout
      />
    );
  }
}

function mapStateToProps(state, ownProps) {
  const { slug, cursor } = ownProps.params;
  const profile = selectProfile(state, slug);
  const profileIsEnsemble = includes(profile.functions, 'ensemble');
  const query = ownProps.location.query;
  const filterParams = selectAlbumsFilterParams(query, profile.id);

  return {
    profileId: profile.id.toString(),
    topFacets: selectAlbumFilterTopFacets(state, filterParams),
    activeFacets: selectActiveAlbumFacets(state, filterParams, 'albums'),
    albums: selectFilteredAndSortedAlbums(state, filterParams),
    location: ownProps.location,
    filterParams,
    nextCursor: selectNextFilteredAlbumsCursor(state, filterParams),
    prevCursor: selectPreviousFilteredAlbumsCursor(state, filterParams),
    sort: selectSortOrder(query, ENTITY_TYPE_ALBUM),
    currentCursor: cursor,
    albumsAreLoading: selectFilteredAndSortedAlbumsLoading(state),
    profileIsEnsemble,
  };
}

function fetchData(store, { slug, cursor }, location) {
  const state = store.getState();
  const profile = selectProfile(state, slug);
  const filterParams = selectAlbumsFilterParams(location.query, profile.id);
  return [
    store.dispatch(loadAlbumsByFilter(filterParams, cursor, __CAPACITOR__ ? 20 : 50)),
    store.dispatch(facetActions.loadTopAlbumFacets(filterParams)),
  ];
}

export default compose(
  dataComponent(fetchData),
  connect(mapStateToProps, {
    loadAlbumsByFilter,
    loadAllAlbumFacets: facetActions.loadAllAlbumFacets,
    analyticsTrack: analyticsActions.track,
  })
)(ProfileAlbums);
